import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsCheckCircle } from 'react-icons/bs';
import { HiViewColumns } from 'react-icons/hi2';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useErrorStore } from '#lib/stores/errorStore';

import { usePipelineStageSelection } from '../../../hooks/usePipelineStageSelection';
import { useCreateDealsFromCrmContacts } from '../../../models/imports/mutations';
import PipelineStageFields from '../PipelineStageFields/PipelineStageFields';

// The wording differs because for CRM-resident contacts we're "just" creating a deal, whereas
// global prospects are pulled into the CRM as well.
const CRM_COPY = {
	modalTitle: 'Als Deal anlegen',
	submitLabel: 'Deals anlegen',
	successTitle: 'Deals angelegt',
	errorTitle: 'Deal-Anlage fehlgeschlagen',
};
const GLOBAL_COPY = {
	modalTitle: 'Ins CRM übertragen',
	submitLabel: 'Ins CRM übertragen',
	successTitle: 'Übertragung abgeschlossen',
	errorTitle: 'Übertragung ins CRM fehlgeschlagen',
};

function resolveContactSource({ tableScope, scopeCount, crmContactIds, contactIds }) {
	if (tableScope) return { copy: CRM_COPY, payload: { scope: tableScope }, count: scopeCount };
	if (crmContactIds) return { copy: CRM_COPY, payload: { crmContactIds }, count: crmContactIds.length };
	const ids = contactIds ?? [];
	return { copy: GLOBAL_COPY, payload: { contactIds: ids }, count: ids.length };
}

/** Result screen shown after the transfer completes: created / skipped / failed counts. */
const TransferSummary = ({ summary }) => (
	<>
		<div className="grid grid-cols-3 gap-3 mb-4">
			<div className="bg-success-200 rounded-card p-3 text-center">
				<div className="text-lg font-bold text-success-700">{summary.created}</div>
				<div className="text-xs text-success-700">Angelegt</div>
			</div>
			<div className="bg-caution-200 rounded-card p-3 text-center">
				<div className="text-lg font-bold text-caution-700">{summary.skipped}</div>
				<div className="text-xs text-caution-700">Übersprungen</div>
			</div>
			<div className={`rounded-card p-3 text-center ${summary.failed > 0 ? 'bg-warning-200' : 'bg-bgBase-300'}`}>
				<div className={`text-lg font-bold ${summary.failed > 0 ? 'text-warning-700' : 'text-bgBase-800'}`}>
					{summary.failed}
				</div>
				<div className={`text-xs ${summary.failed > 0 ? 'text-warning-700' : 'text-bgBase-800'}`}>
					Fehlgeschlagen
				</div>
			</div>
		</div>

		{summary.failed > 0 && (
			<Alert tone="warning" className="p-3">
				Einige Empfänger konnten nicht importiert werden. Details finden Sie in den Server-Logs.
			</Alert>
		)}
	</>
);

TransferSummary.propTypes = {
	summary: PropTypes.shape({
		requested: PropTypes.number,
		created: PropTypes.number,
		skipped: PropTypes.number,
		failed: PropTypes.number,
	}).isRequired,
};

/**
 * Modal for transferring contacts into deals on a selected pipeline/stage.
 *
 * Accepts one source: `crmContactIds` (contacts already in the CRM), `contactIds`
 * (prospects from /leads — backend auto-materialises them into the CRM first) or a table
 * selection `tableScope` + `count` (contact-list contacts, resolved by the backend).
 */

const TransferContactsToCrmModal = ({
	isOpen,
	onClose,
	crmContactIds = null,
	contactIds = null,
	tableScope = null,
	count: scopeCount = 0,
}) => {
	const {
		copy,
		payload: sourcePayload,
		count,
	} = resolveContactSource({
		tableScope,
		scopeCount,
		crmContactIds,
		contactIds,
	});
	const selection = usePipelineStageSelection({ isOpen });
	const [skipDuplicates, setSkipDuplicates] = useState(true);
	const [summary, setSummary] = useState(null);

	const importMutation = useCreateDealsFromCrmContacts();
	const showError = useErrorStore((s) => s.showError);
	const showSuccess = useErrorStore((s) => s.showSuccess);

	const handleClose = () => {
		setSummary(null);
		onClose();
	};

	const handleSubmit = async () => {
		const { pipelineId, stageId } = selection;
		try {
			const result = await importMutation.mutateAsync({
				...sourcePayload,
				pipelineId,
				stageId,
				skipDuplicates,
			});

			handleClose();

			const { created = 0, skipped = 0, failed = 0 } = result.summary ?? {};
			const parts = [];
			if (created > 0) parts.push(`${created} angelegt`);
			if (skipped > 0) parts.push(`${skipped} übersprungen`);
			if (failed > 0) parts.push(`${failed} fehlgeschlagen`);

			showSuccess({ title: copy.successTitle, message: `${parts.join(', ')}.` });
		} catch (err) {
			showError({ error: err, title: copy.errorTitle });
		}
	};

	if (!isOpen) return null;

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="m">
			{summary ? (
				<Panel.Header
					icon={BsCheckCircle}
					onClose={handleClose}
					subtitle={`${summary.requested} Empfänger verarbeitet.`}
				>
					{copy.successTitle}
				</Panel.Header>
			) : (
				<Panel.Header
					icon={HiViewColumns}
					onClose={handleClose}
					subtitle={`${count} ${count === 1 ? 'Empfänger wird' : 'Empfänger werden'} als Deal angelegt.`}
				>
					{copy.modalTitle}
				</Panel.Header>
			)}
			{summary ? (
				<Panel.Body>
					<TransferSummary summary={summary} />
				</Panel.Body>
			) : (
				<Panel.Body>
					<PipelineStageFields selection={selection} />
					{!selection.isLoading && selection.pipelines.length > 0 && (
						<div className="pt-3 mt-4 border-t border-bgBase-400">
							<Checkbox
								id="transfer-contacts-skip-duplicates"
								checked={skipDuplicates}
								onChange={setSkipDuplicates}
								label="Empfänger überspringen, die bereits einen offenen Deal haben"
							/>
						</div>
					)}
				</Panel.Body>
			)}
			{summary ? (
				<Panel.Footer className="justify-end">
					<Button variant="solid" onClick={handleClose}>
						Schließen
					</Button>
				</Panel.Footer>
			) : (
				<Panel.Footer className="justify-end">
					<Button variant="outline" onClick={handleClose}>
						Abbrechen
					</Button>
					<Button
						variant="solid"
						onClick={handleSubmit}
						disabled={!selection.isReady || selection.isLoading || count === 0}
						loading={importMutation.isPending}
					>
						{copy.submitLabel}
					</Button>
				</Panel.Footer>
			)}
		</Modal>
	);
};

TransferContactsToCrmModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	crmContactIds: PropTypes.arrayOf(PropTypes.string),
	contactIds: PropTypes.arrayOf(PropTypes.string),
	// Contacts of a contact list: ids (crmContactIds) XOR selectAll + filters.
	tableScope: PropTypes.shape({
		table: PropTypes.oneOf(['contactListContacts']).isRequired,
		contextId: PropTypes.string.isRequired,
		ids: PropTypes.arrayOf(PropTypes.string),
		selectAll: PropTypes.bool,
		filters: PropTypes.object,
	}),
	// Number of contacts tableScope covers.
	count: PropTypes.number,
};

export default TransferContactsToCrmModal;
